import { IconActivity, IconGlobe, IconHelpCircle, IconInfo, IconMessageSquare } from 'featherico';
import moment from 'moment';
import type React from 'react';
import type { CurrentUserDto } from '../../../../../../types/UserDtos';
import Button from '../../../../components/Button';
import { DropdownMenu, MenuItem, MenuLink, type MenuPlacement } from '../../../../components/DropdownMenu';
import Modal from '../../../../components/Modal';
import ActivityDot from '../../../../components/Notifications/Dot/ActivityDot';
import { useAppContext } from '../../../../hooks/useAppContext';
import useLocalStorage from '../../../../hooks/useLocalStorage';
import { useNow } from '../../../../hooks/useNow';
import { useBooleanState } from '../../../../hooks/useToggle';
import { isAnyAdmin } from '../../../../services/User';
import { JustLaunchedMenuItems } from '../../../JustLaunched/MenuItems';
import { getActiveReleaseNotes } from '../../../JustLaunched/service';
import { openIntercomSupport } from '../../../Navigation/SupportLink/IntercomSupportLink';
import { HrListSupport } from '../../../Navigation/SupportLink/WithHrListSupportModal';
import styles from './style.m.less';

const RELEASE_NOTES_LAST_VIEWED_STORAGE_KEY = 'release-notes-last-viewed';

type Props = {
    user: CurrentUserDto;
    now: Date;
    releaseNotes: ReturnType<typeof useReleaseNotes>;
    trigger: React.ReactElement;
    placement: MenuPlacement;
};

export default function HelpMenuButton() {
    const { user } = useAppContext();
    const now = useNow(moment.duration(1, 'hour'));
    const releaseNotes = useReleaseNotes(user, now);
    return (
        <HelpMenu
            user={user}
            now={now}
            releaseNotes={releaseNotes}
            trigger={
                <Button kind="PLAIN" aria-label="Help menu">
                    <div className={styles.iconContainer}>
                        <IconHelpCircle />
                        {releaseNotes.hasNewReleaseNotes && (
                            <div className={styles.dotContainer}>
                                <ActivityDot />
                            </div>
                        )}
                    </div>
                </Button>
            }
            placement="top-start"
        />
    );
}

export function HelpMenu({ user, now, releaseNotes, trigger, placement }: Props) {
    const [showSupportModal, onOpenSupportModal, onCloseSupportModal] = useBooleanState();

    return (
        <>
            <Modal isOpen={showSupportModal} onClose={onCloseSupportModal} title="Get Support" size="LG_WIDTH">
                <HrListSupport />
            </Modal>
            <DropdownMenu
                trigger={trigger}
                eventTracking={{ category: 'navigation header help', action: 'toggle' }}
                placement={placement}
            >
                <MenuLink
                    href="https://intercomdocs.small-improvements.com/en/"
                    openInNewTab={true}
                    eventTracking={{ category: 'navigation header help', action: 'click', label: 'help center' }}
                >
                    <IconInfo /> Help Center
                </MenuLink>
                {isAnyAdmin(user) ? (
                    <MenuItem
                        onClick={openIntercomSupport}
                        eventTracking={{
                            category: 'navigation header help',
                            action: 'click',
                            label: 'get help as admin: intercom',
                        }}
                    >
                        <IconMessageSquare /> Get Support
                    </MenuItem>
                ) : (
                    <MenuItem
                        onClick={onOpenSupportModal}
                        eventTracking={{
                            category: 'navigation header help',
                            action: 'click',
                            label: 'get help non-admin',
                        }}
                    >
                        <IconMessageSquare /> Get Support
                    </MenuItem>
                )}
                <MenuLink
                    href="https://status.small-improvements.com"
                    openInNewTab={true}
                    eventTracking={{ category: 'navigation header help', action: 'click', label: 'system status' }}
                >
                    <IconActivity /> System Status
                </MenuLink>
                <MenuLink
                    href="https://small-improvements.com"
                    openInNewTab={true}
                    eventTracking={{ category: 'navigation header help', action: 'click', label: 'si home page' }}
                >
                    <IconGlobe /> Website
                </MenuLink>
                <JustLaunchedMenuItems
                    now={now}
                    lastViewed={releaseNotes.lastViewed}
                    onHide={releaseNotes.updateLastViewed}
                />
            </DropdownMenu>
        </>
    );
}

export function useReleaseNotes(currentUser: CurrentUserDto, now: Date) {
    'use memo';
    // Use “last login” date as initializer, to avoid bothering people with old release notes.
    // See the annotation comment on `previousLoginDate` in `User.java` for why we are not
    // using `lastLoginDate` here.
    const [lastViewedVal, setLastViewedVal] = useLocalStorage(
        RELEASE_NOTES_LAST_VIEWED_STORAGE_KEY,
        new Date(currentUser.previousLoginDate ?? 0).toISOString()
    );
    const lastViewed = new Date(lastViewedVal);
    return {
        lastViewed,
        updateLastViewed: () => setLastViewedVal(new Date().toISOString()),
        hasNewReleaseNotes:
            getActiveReleaseNotes(now.getTime()).find(r => new Date(r.date).getTime() > lastViewed.getTime()) !==
            undefined,
    };
}
